import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, of, combineLatest } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

import { BillableItem, UserSubscription } from 'modules/shared/models/billable.models';
import { LoadBillablePayload } from 'store/models/billable.models';
import { Domain } from '../models/domain.models';
import { Role } from '../models/role.models';
import { AuthService } from 'modules/core/services/auth.service';

import { Selectors, AppState, Actions } from '../../../store/root-reducer';

@Injectable()
export class AuthGuard implements CanActivate {
  constructor(private authService: AuthService,
              private router: Router,
              private store: Store<AppState>) { }

  canActivate(router: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.authService.getUser(true)
      .pipe(
        switchMap(() => {
          this.store.dispatch(new Actions.auth.GetUser({ bypassCache: true }));
          return combineLatest([
            this.store.select(Selectors.billable.getSearchEngines),
            this.store.select(Selectors.billable.getSubscriptions),
            this.store.select(Selectors.domain.getCurrentDomain),
            this.store.select(Selectors.auth.getUserRoles)
          ]);
        }),
        switchMap(([billItem, userSubscription, domain, roles]: [Array<BillableItem>, Array<UserSubscription>, Domain, Array<Role>]) => {
          if (billItem.length === 0 && userSubscription.length === 0) {
            this.store.dispatch(new Actions.billable.LoadBillable(<LoadBillablePayload>{ quietUpdate: true }));
          }
          if (roles.length === 0 && domain) {
            this.store.dispatch(new Actions.auth.GetUserRoles({ domainId: domain.id, currentPath: state.url }));
          }
          if (!domain && state.url.indexOf('select-domain') === -1 && state.url.indexOf('mfa/setup') === -1) {
            this.router.navigateByUrl('select-domain');
          }
          return of(true);
        }),
        catchError(() => {
          this.store.dispatch(new Actions.auth.LogInRequired());
          return of(false);
        })
      );
    }
}
